import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Heart, Loader2, Lock } from "lucide-react";
import { getJson, postJson } from "../../lib/http";

import { API_BASE } from "../../lib/apiBase";
import { QVOLT_LOGO } from "../../lib/content";

/**
 * Phase 10 — pre-launch site gate.
 *
 * Wraps the entire SPA. On mount, calls /api/site-gate/status. If the gate is
 * enabled server-side AND the visitor does not hold a valid signed cookie,
 * renders <ComingSoon /> which collects the shared team password.
 *
 * Performance note — the gate is OPTIMISTIC. The site has been live (gate
 * disabled) since launch, yet the previous implementation held the whole app
 * behind a spinner until the status round-trip finished, which added a full
 * network request to First/Largest Contentful Paint on every page view. Now
 * the page renders immediately and the status check runs in the background;
 * only a locked response swaps the content for the password screen. While
 * the gate is enabled a locked visitor may glimpse the page for a moment —
 * acceptable for a shared-password pre-launch curtain.
 *
 * Once SITE_GATE_ENABLED=false in backend/.env (post-launch), this whole tree
 * just renders children. No code change needed to disable the gate.
 */
export const SiteGate = ({ children }) => {
  // null = not yet checked (render children optimistically)
  const [status, setStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getJson(`${API_BASE}/site-gate/status`, {
          withCredentials: true,
          timeout: 10000,
        });
        if (!cancelled) setStatus(data);
      } catch (_) {
        // Fail-open: if the gate API errors, don't block the site.
        if (!cancelled) {
          setStatus({ unlocked: true, gate_enabled: false, error: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const onUnlock = useCallback(() => {
    setStatus(null);
    setRefreshKey((k) => k + 1);
  }, []);

  if (status === null || status.unlocked) return children;
  return <ComingSoon onUnlock={onUnlock} />;
};

const ComingSoon = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting || !value) return;
      setSubmitting(true);
      setError(null);
      try {
        await postJson(
          `${API_BASE}/site-gate/unlock`,
          { password: value },
          { withCredentials: true, timeout: 10000 }
        );
        // Re-check status so the cookie is reflected in our state.
        onUnlock();
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401) {
          setError("That's not the right password. Try again.");
        } else if (code === 429) {
          setError("Too many attempts. Please wait a few minutes.");
        } else {
          setError("Couldn't reach the server. Please try again.");
        }
        setValue("");
        setSubmitting(false);
      }
    },
    [submitting, value, onUnlock]
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-muted px-4 py-12"
      data-testid="site-gate-coming-soon"
    >
      <Helmet>
        <title>QVolt Solar — Coming Soon</title>
        <meta name="description" content="Site under construction." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div
        className="w-full max-w-md bg-white rounded-2xl border border-border shadow-sm p-10 md:p-12"
        data-testid="site-gate-card"
      >
        <div className="flex justify-center mb-7">
          <img
            src={QVOLT_LOGO}
            alt="QVolt Solar"
            className="h-14 w-auto"
            data-testid="site-gate-logo"
          />
        </div>
        <div className="text-center">
          <div className="qv-eyebrow text-brand-blue mb-3">Coming soon</div>
          <h1
            className="font-display text-[1.625rem] md:text-[1.875rem] leading-[1.15] tracking-tight text-ink"
            data-testid="site-gate-headline"
          >
            We're putting the finishing touches on this site.
          </h1>
          <p className="qv-body mt-4 text-ink/80">
            Visit us at{" "}
            <a
              href="https://qvoltsolar.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-blue underline"
            >
              qvoltsolar.com.au
            </a>{" "}
            or enter the team password below.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-8" noValidate>
          <label htmlFor="site-gate-password" className="sr-only">
            Team password
          </label>
          <div className="relative">
            <Lock
              className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle pointer-events-none"
              strokeWidth={1.5}
            />
            <input
              id="site-gate-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              spellCheck="false"
              placeholder="Team password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="site-gate-input"
              className="w-full h-12 pl-10 pr-3.5 rounded-full bg-white border border-border text-[15px] text-ink focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="site-gate-submit"
            className="mt-4 w-full h-12 rounded-full bg-brand-blue text-white font-medium text-[15px] inline-flex items-center justify-center gap-2 hover:bg-brand-blue-dark transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <>
                Unlock site
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </>
            )}
          </button>
          {error && (
            <p
              className="mt-3 text-[13px] text-red-600 text-center"
              role="alert"
              data-testid="site-gate-error"
            >
              {error}
            </p>
          )}
        </form>

        <p
          className="mt-8 pt-6 border-t border-border text-center text-[12.5px] text-subtle inline-flex items-center justify-center gap-1.5 w-full"
          data-testid="site-gate-footer"
        >
          QVolt Solar — Honest, family-owned solar in QLD and NSW
          <Heart className="h-3 w-3 text-brand-orange" strokeWidth={2} />
        </p>
      </div>
    </div>
  );
};

export default SiteGate;
